import React, { useState, useEffect, useRef } from 'react';
import { testBotFlow } from '../../services/api';

function buildBotMessageFromNode(node) {
    if (!node) return null;

    const presentation = node.type === 'menu'
        ? (node.presentation || (node.options?.length ? 'buttons' : 'text'))
        : 'text';

    const interactive = presentation === 'text'
        ? null
        : {
            type: presentation,
            text: node.message || '',
            header_text: node.header_text || '',
            footer_text: node.footer_text || '',
            action_title: node.action_title || 'View options',
            section_title: node.section_title || node.label || 'Options',
            options: (node.options || []).map(opt => ({
                id: opt.input,
                title: opt.label || opt.input,
                description: opt.description || '',
            })),
        };

    return {
        role: 'bot',
        text: node.message || '',
        responseType: presentation,
        interactive,
    };
}

export default function FlowTestModal({ flow, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [currentNodeId, setCurrentNodeId] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const chatEndRef = useRef(null);

    useEffect(() => {
        // Show initial root node message
        if (flow) {
            const rootNode = flow.nodes.find(n => n.node_id === flow.root_node_id);
            if (rootNode) {
                setMessages([buildBotMessageFromNode(rootNode)]);
                setCurrentNodeId(flow.root_node_id);
            }
        }
    }, [flow]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (rawValue = input, displayText = null) => {
        const trimmed = (rawValue || '').trim();
        if (!trimmed || isSending) return;

        setMessages(prev => [...prev, { role: 'user', text: displayText || trimmed }]);
        if (rawValue === input) {
            setInput('');
        }
        setIsSending(true);

        try {
            const res = await testBotFlow(trimmed, currentNodeId, flow);
            const data = res.data;
            setMessages(prev => [...prev, {
                role: 'bot',
                text: data.response,
                responseType: data.response_type || 'text',
                interactive: data.interactive || null,
            }]);
            setCurrentNodeId(data.next_node_id);
        } catch (err) {
            setMessages(prev => [...prev, {
                role: 'bot',
                text: '⚠️ Test error: ' + (err.response?.data?.detail || err.message),
                error: true,
            }]);
        } finally {
            setIsSending(false);
        }
    };

    const handleReset = () => {
        const rootNode = flow.nodes.find(n => n.node_id === flow.root_node_id);
        setMessages(rootNode ? [buildBotMessageFromNode(rootNode)] : []);
        setCurrentNodeId(flow.root_node_id);
        setInput('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const currentNode = flow?.nodes?.find(n => n.node_id === currentNodeId);

    const renderInteractiveOptions = (msg) => {
        const options = msg?.interactive?.options || [];
        if (!options.length) return null;

        return (
            <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
                {options.map((option) => (
                    <button
                        key={option.id}
                        type="button"
                        className="btn-secondary-sm"
                        style={{ textAlign: 'left' }}
                        disabled={isSending}
                        onClick={() => handleSend(option.id, option.title)}
                    >
                        <div>{option.title}</div>
                        {option.description ? (
                            <div style={{ fontSize: '0.82rem', opacity: 0.75 }}>{option.description}</div>
                        ) : null}
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="test-modal" onClick={e => e.stopPropagation()}>
                <div className="test-modal-header">
                    <h2>Test Your Bot Flow</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className="test-chat">
                    {messages.map((msg, i) => (
                        <div
                            key={i}
                            className={`test-msg ${msg.role} ${msg.error ? 'error' : ''}`}
                        >
                            <span className="test-msg-label">{msg.role === 'bot' ? 'Bot' : 'You'}</span>
                            <div className="test-msg-text">{msg.text}</div>
                            {msg.role === 'bot' && msg.interactive?.header_text ? (
                                <div style={{ marginTop: '6px', fontSize: '0.82rem', fontWeight: 600 }}>
                                    Header: {msg.interactive.header_text}
                                </div>
                            ) : null}
                            {msg.role === 'bot' && msg.responseType === 'list' && msg.interactive?.action_title ? (
                                <div style={{ marginTop: '6px', fontSize: '0.82rem', opacity: 0.75 }}>
                                    List button: {msg.interactive.action_title}
                                </div>
                            ) : null}
                            {msg.role === 'bot' ? renderInteractiveOptions(msg) : null}
                            {msg.role === 'bot' && msg.interactive?.footer_text ? (
                                <div style={{ marginTop: '8px', fontSize: '0.78rem', opacity: 0.75 }}>
                                    Footer: {msg.interactive.footer_text}
                                </div>
                            ) : null}
                        </div>
                    ))}
                    <div ref={chatEndRef} />
                </div>

                <div className="test-status">
                    <span>Current Node: <code>{currentNode?.label || currentNodeId || 'none'}</code></span>
                    <button className="btn-secondary-sm" onClick={handleReset}>Reset</button>
                </div>

                <div className="test-input-row">
                    <input
                        type="text"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message..."
                        disabled={isSending}
                        autoFocus
                    />
                    <button
                        className="btn-primary-sm"
                        onClick={handleSend}
                        disabled={isSending || !input.trim()}
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
}
